import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Stack,
  useTheme,
  Menu,
  MenuItem,
  Dialog,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Task as TaskIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  PriorityHigh as PriorityIcon,
  Pause as PauseIcon,
  CheckCircle as CompleteIcon,
  Block as BlockIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import {
  fetchTasks,
  fetchTeamTasks,
  deleteExistingTask,
  updateExistingTaskStatus,
} from "store/features/company/projectManagement/task/taskActions";
import {
  selectTasks,
  selectTaskLoading,
} from "store/features/company/projectManagement/task/taskSelectors";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import AddTaskModal from "./AddTaskModal";
import EditTaskModal from "./EditTaskModal";
import TaskReviewModal from "./TaskReviewModal";
import TaskBoard from "./TaskBoard";
import TaskCalendar from "./TaskCalendar";

import taskSvg from "assets/folders/task.svg";
import { usePermissions } from "hooks/usePermissions";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { showErrorToast } from "utils/toastNotification";

const Task = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const tasks = useSelector(selectTasks);
  const loading = useSelector(selectTaskLoading);
  const role = getSecureLocalItem("role") || "unknown";

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [taskScope, setTaskScope] = useState(
    role === "Employee" ? "assigned_to_me" : "all",
  );
  const [viewMode, setViewMode] = useState("board");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const [projectFilter, setProjectFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("7d");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const projects = useSelector(selectProjects);
  const employees = useSelector(selectEmployees);

  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);

  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const { hasPermission: rawHasPermission } = usePermissions();
  const hasPermission = (permission) =>
    rawHasPermission(permission) || role === "Company";
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const [reviewTaskData, setReviewTaskData] = useState(null);

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchEmployees());
  }, [dispatch]);

  const loadTasks = useCallback(() => {
    const params = {};
    if (projectFilter) params.project_id = projectFilter;
    if (assignedToFilter) params.assigned_to = assignedToFilter;
    if (dateRangeFilter) params.date_range = dateRangeFilter;

    if (taskScope === "teams") {
      dispatch(fetchTeamTasks(Object.keys(params).length ? params : null));
      return;
    }

    if (role === "Employee") {
      if (taskScope === "assigned_to_me") params.assigned_to_me = true;
      if (taskScope === "created_by_me") params.created_by_me = true;
    }

    dispatch(fetchTasks(Object.keys(params).length ? params : null));
  }, [dispatch, projectFilter, assignedToFilter, taskScope, role, dateRangeFilter]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleStatusMenuOpen = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTaskForStatus(task);
  };

  const handleStatusMenuClose = () => {
    setAnchorEl(null);
    setSelectedTaskForStatus(null);
  };

  const handleStatusChange = async (statusOrEvent, taskOrNull) => {
    let statusToUpdate;
    let taskToUpdate;

    if (typeof statusOrEvent === "string" && taskOrNull) {
      statusToUpdate = statusOrEvent;
      taskToUpdate = taskOrNull;
    } else if (statusOrEvent && statusOrEvent.target) {
      statusToUpdate = statusOrEvent.target.value || statusOrEvent;
      taskToUpdate = taskOrNull || selectedTaskForStatus;
    } else if (selectedTaskForStatus && typeof statusOrEvent === "string") {
      statusToUpdate = statusOrEvent;
      taskToUpdate = selectedTaskForStatus;
    }

    if (taskToUpdate && statusToUpdate) {
      // Rule 1: Task cannot be marked as done without starting
      if (statusToUpdate === "done" && (taskToUpdate.status === "todo" || taskToUpdate.current_status === "todo")) {
        showErrorToast("Task cannot be marked as done without starting it first.");
        handleStatusMenuClose();
        return;
      }

      // Rule 2: Only one task in progress at a time
      if (statusToUpdate === "in_progress") {
        const otherInProgress = tasks.find(
          (t) => t.id !== taskToUpdate.id && t.status === "in_progress" && (t.assigned_to === taskToUpdate.assigned_to)
        );
        if (otherInProgress) {
          showErrorToast(`Only one task can be in progress at a time. "${otherInProgress.title}" is currently in progress.`);
          handleStatusMenuClose();
          return;
        }
      }

      if (
        statusToUpdate === "blocked" ||
        statusToUpdate === "paused" ||
        statusToUpdate === "review" ||
        statusToUpdate === "done"
      ) {
        setPendingStatusChange({
          taskId: taskToUpdate.id,
          status: statusToUpdate,
        });
        setReviewTaskData({
          taskId: taskToUpdate.id,
          status: statusToUpdate,
        });
        setReviewModalOpen(true);

        handleStatusMenuClose();
        return;
      }
      await dispatch(
        updateExistingTaskStatus(taskToUpdate.id, {
          status: statusToUpdate,
        }),
      );
      window.dispatchEvent(new Event("task_status_changed"));
    }
    handleStatusMenuClose();
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (taskToDelete) {
      setDeleting(true);
      const result = await dispatch(deleteExistingTask(taskToDelete.id));
      if (result.success) {
        loadTasks();
        setDeleteDialogOpen(false);
        setTaskToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project ? project.name : `Project ${projectId}`;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee
      ? `${employee.first_name} ${employee.last_name}`
      : ` ${employeeId}`;
  };

  const getTaskDescription = (task) =>
    task?.description ||
    task?.task_description ||
    task?.details ||
    task?.detail ||
    task?.notes ||
    "";

  const filteredTasks = tasks.filter((task) => {
    const description = getTaskDescription(task);
    const matchesSearch =
      searchTerm === "" ||
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || task.status === statusFilter;

    const matchesPriority =
      priorityFilter === "all" || task.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case "todo":
        return "To Do";
      case "in_progress":
        return "In Progress";
      case "review":
        return "Review";
      case "done":
        return "Done";
      case "paused":
        return "Paused";
      case "blocked":
        return "Blocked";
      default:
        return status || "N/A";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "todo":
        return { bg: "#fef3c7", color: "#92400e" };
      case "in_progress":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "review":
        return { bg: "#f3e8ff", color: "#6b21a5" };
      case "done":
        return { bg: "#d1fae5", color: "#065f46" };
      case "paused":
        return { bg: "#fef3c7", color: "#92400e" };
      case "blocked":
        return { bg: "#fee2e2", color: "#991b1b" };
      default:
        return { bg: "#e5e7eb", color: "#374151" };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "#dc2626";
      case "high":
        return "#f97316";
      case "medium":
        return "#eab308";
      case "low":
        return "#22c55e";
      default:
        return "#9ca3af";
    }
  };

  const getPriorityIcon = (priority) => {
    return null;
  };

  if (!hasPermission("tasks.view")) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "todo":
        return <ScheduleIcon sx={{ fontSize: 14 }} />;
      case "in_progress":
        return <PlayIcon sx={{ fontSize: 14 }} />;
      case "review":
        return <PriorityIcon sx={{ fontSize: 14 }} />;
      case "done":
        return <CompleteIcon sx={{ fontSize: 14 }} />;
      case "paused":
        return <PauseIcon sx={{ fontSize: 14 }} />;
      case "blocked":
        return <BlockIcon sx={{ fontSize: 14 }} />;
      default:
        return <TaskIcon sx={{ fontSize: 14 }} />;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${day}/${month}/${String(year).slice(-2)}`;
  };

  const scrollBoard = (direction) => {
    const board = document.getElementById("task-board-container");

    if (board) {
      board.scrollBy({
        left: direction * 350,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={taskSvg}
              alt="Tasks"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Tasks
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage your project tasks
              </Typography>
            </Box>
          </Stack>

          <Box>
            {hasPermission("tasks.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Task
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Task Scope Toggle Section - Only for Employees */}
      {role === "Employee" && (
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={taskScope}
            exclusive
            onChange={(e, newScope) => {
              if (newScope !== null) {
                setTaskScope(newScope);
              }
            }}
            aria-label="task scope"
            sx={{
              bgcolor: "background.paper",
              borderRadius: "12px",
              padding: "4px",
              border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px !important",
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: 600,
                color: "text.secondary",
                margin: "0 4px",
                "&.Mui-selected": {
                  backgroundColor: "rgba(245, 158, 11, 0.1)",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(245, 158, 11, 0.2)",
                  },
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                },
              },
            }}
          >
            <ToggleButton value="assigned_to_me">Assigned to Me</ToggleButton>
            <ToggleButton value="created_by_me">Created by Me</ToggleButton>
            <ToggleButton value="teams">Teams</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          aria-label="task view mode"
          sx={{
            gap: "6px",
            bgcolor: "background.paper",
            borderRadius: "12px",
            padding: "4px",
            border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
              }`,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "8px !important",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: 700,
              color: "text.secondary",
              "&.Mui-selected": {
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                color: "primary.main",
              },
            },
          }}
        >
          <ToggleButton value="board">Board</ToggleButton>
          <ToggleButton value="calendar">Calendar</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Search and Filter Section */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 0.7,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="todo">To Do</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="review">Review</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="done">Done</MenuItem>
          <MenuItem value="blocked">Blocked</MenuItem>
        </TextField>

        <TextField
          select
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Priorities</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </TextField>

        <TextField
          select
          label="Project"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {projects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name} ({project.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Assigned To"
          value={assignedToFilter}
          onChange={(e) => setAssignedToFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name} ({emp.employee_code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Date Range"
          value={dateRangeFilter}
          onChange={(e) => setDateRangeFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 165 }}
        >
          <MenuItem value="7d">Last 7 Days (Default)</MenuItem>
          <MenuItem value="1m">Last 1 Month</MenuItem>
          <MenuItem value="3m">Last 3 Months</MenuItem>
          <MenuItem value="all">All Tasks</MenuItem>
        </TextField>

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton onClick={() => scrollBoard(-1)}>
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton onClick={() => scrollBoard(1)}>
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>

        {(searchTerm ||
          statusFilter !== "all" ||
          priorityFilter !== "all" ||
          projectFilter ||
          assignedToFilter ||
          dateRangeFilter !== "7d") && (
            <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setPriorityFilter("all");
                setProjectFilter("");
                setAssignedToFilter("");
                setDateRangeFilter("7d");
              }}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: "8px",
                color: "primary.main",
                borderColor: "#f59e0b",
                whiteSpace: "nowrap",
              }}
            >
              Clear Filters
            </Button>
          )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "primary.main" }} />
            <Typography sx={{ color: "text.secondary" }}>
              Loading tasks...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
          {/* Task View */}
          {filteredTasks.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: "16px",
                bgcolor: "background.paper",
              }}
            >
              <TaskIcon sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }} />
              <Typography sx={{ color: "text.secondary" }}>
                No tasks found. Click "Add Task" to get started.
              </Typography>
            </Paper>
          ) : viewMode === "calendar" ? (
            <TaskCalendar
              tasks={filteredTasks}
              projects={projects}
              employees={employees}
              taskScope={taskScope}
              onEditTask={(task) => {
                setSelectedTask(task);
                setShowEditModal(true);
              }}
              onDeleteTask={handleDeleteClick}
              onStatusChange={handleStatusMenuOpen}
              onTaskClick={(task) => {
                setSelectedTaskForDetails(task);
              }}
              getProjectName={getProjectName}
              getEmployeeName={getEmployeeName}
              getTaskDescription={getTaskDescription}
              getStatusLabel={getStatusLabel}
              getStatusColor={getStatusColor}
              getPriorityColor={getPriorityColor}
              getPriorityIcon={getPriorityIcon}
              hasPermission={hasPermission}
            />
          ) : (
            <TaskBoard
              tasks={filteredTasks}
              projects={projects}
              employees={employees}
              onEditTask={(task) => {
                setSelectedTask(task);
                setShowEditModal(true);
              }}
              onDeleteTask={handleDeleteClick}
              onStatusChange={handleStatusChange}
              onTaskClick={(task) => {
                setSelectedTaskForDetails(task);
              }}
              getProjectName={getProjectName}
              getEmployeeName={getEmployeeName}
              getTaskDescription={getTaskDescription}
              getStatusLabel={getStatusLabel}
              getStatusColor={getStatusColor}
              getPriorityColor={getPriorityColor}
              getPriorityIcon={getPriorityIcon}
              hasPermission={hasPermission}
            />
          )}

          {/* Count Summary */}
          {!loading && tasks.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Total Tasks: {tasks.length}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing: {filteredTasks.length} of {tasks.length}
              </Typography>
            </Stack>
          )}
        </>
      )}

      {/* Status Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleStatusMenuClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 150,
            bgcolor: "background.paper",
          },
        }}
      >
        <MenuItem
          onClick={() => handleStatusChange("todo", selectedTaskForStatus)}
        >
          <ScheduleIcon sx={{ fontSize: 18, mr: 1, color: "#92400e" }} />
          To Do
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleStatusChange("in_progress", selectedTaskForStatus)
          }
        >
          <PlayIcon sx={{ fontSize: 18, mr: 1, color: "#1e40af" }} />
          In Progress
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange("review", selectedTaskForStatus)}
        >
          <PriorityIcon sx={{ fontSize: 18, mr: 1, color: "#6b21a5" }} />
          Review
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange("paused", selectedTaskForStatus)}
        >
          <PauseIcon sx={{ fontSize: 18, mr: 1, color: "#065f46" }} />
          Paused
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange("done", selectedTaskForStatus)}
        >
          <CompleteIcon sx={{ fontSize: 18, mr: 1, color: "#53ff24" }} />
          Done
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange("blocked", selectedTaskForStatus)}
        >
          <BlockIcon sx={{ fontSize: 18, mr: 1, color: "#381010" }} />
          Blocked
        </MenuItem>
      </Menu>

      {/* Modals */}
      <AddTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => loadTasks()}
      />

      <TaskReviewModal
        open={reviewModalOpen}
        taskData={reviewTaskData}
        pendingStatusChange={pendingStatusChange}
        loadTasks={loadTasks}
        onClose={() => {
          setReviewModalOpen(false);
          setReviewTaskData(null);
          setPendingStatusChange(null);
        }}
      />

      <EditTaskModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onSuccess={() => loadTasks()}
      />

      {/* Task Detail Dialog */}
      <Dialog
        open={Boolean(selectedTaskForDetails)}
        onClose={() => setSelectedTaskForDetails(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#111827" : "#ffffff",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={() => setSelectedTaskForDetails(null)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              color: theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Stack spacing={2.5}>
            <Box sx={{ pr: 5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#d97706",
                  fontWeight: 900,
                  textTransform: "uppercase",
                }}
              >
                Task #{selectedTaskForDetails?.id}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 0.5,
                  fontWeight: 900,
                  color: theme.palette.mode === "dark" ? "#f8fafc" : "#111827",
                }}
              >
                {selectedTaskForDetails?.title}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={getStatusIcon(selectedTaskForDetails?.status)}
                label={getStatusLabel(selectedTaskForDetails?.status)}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(
                    selectedTaskForDetails?.status,
                  ).bg,
                  color: getStatusColor(selectedTaskForDetails?.status).color,
                  fontWeight: 800,
                  "& .MuiChip-icon": {
                    color: getStatusColor(selectedTaskForDetails?.status).color,
                    fontSize: 14,
                  },
                }}
              />
              <Chip
                icon={getPriorityIcon(selectedTaskForDetails?.priority)}
                label={selectedTaskForDetails?.priority || "Priority"}
                size="small"
                sx={{
                  border: `1px solid ${getPriorityColor(
                    selectedTaskForDetails?.priority,
                  )}`,
                  backgroundColor: "transparent",
                  color: getPriorityColor(selectedTaskForDetails?.priority),
                  fontWeight: 800,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <DescriptionIcon sx={{ mt: 0.25, color: "primary.main" }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    color:
                      theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    color:
                      theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {getTaskDescription(selectedTaskForDetails) ||
                    "No description provided"}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {[
                {
                  icon: <BusinessIcon sx={{ color: "primary.main" }} />,
                  label: "Project",
                  value: getProjectName(selectedTaskForDetails?.project_id),
                },
                {
                  icon: <PersonIcon sx={{ color: "primary.main" }} />,
                  label: "Assigned To",
                  value: selectedTaskForDetails?.assigned_to
                    ? getEmployeeName(selectedTaskForDetails?.assigned_to)
                    : "Multiple Asignees",
                },
                {
                  icon: <PersonIcon sx={{ color: "primary.main" }} />,
                  label: "Created By",
                  value: selectedTaskForDetails?.created_by_name
                    ? getEmployeeName(selectedTaskForDetails?.created_by_name)
                    : "N/A",
                },
                {
                  icon: <AccessTimeIcon sx={{ color: "primary.main" }} />,
                  label: "Timeline",
                  value: `${formatDateShort(
                    selectedTaskForDetails?.start_date,
                  )} - ${formatDateShort(selectedTaskForDetails?.due_date)}`,
                },
                {
                  icon: <PriorityIcon sx={{ color: "primary.main" }} />,
                  label: "Mode of Assigning",
                  value: selectedTaskForDetails?.mode_of_assigning || "—",
                },
              ].map((detail) => (
                <Stack
                  key={detail.label}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
                  }}
                >
                  {detail.icon}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        textTransform: "uppercase",
                        color:
                          theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                      }}
                    >
                      {detail.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontWeight: 700,
                        color:
                          theme.palette.mode === "dark" ? "#f8fafc" : "#111827",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {detail.value}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>

            {/* Attachments Section */}
            {selectedTaskForDetails?.attachments?.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    color:
                      theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                    mb: 1.5,
                  }}
                >
                  Attachments ({selectedTaskForDetails.attachments.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {selectedTaskForDetails.attachments.map(
                    (attachment, index) => {
                      let url, mime, name;

                      if (typeof attachment === "string") {
                        url = attachment;
                        name = url.split("/").pop() || "file";
                        mime = url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)
                          ? "image/" + url.split(".").pop()
                          : "application/octet-stream";
                      } else {
                        url = attachment.url;
                        mime = attachment.mime || "";
                        name =
                          attachment.name || url.split("/").pop() || "file";
                      }

                      const isImage =
                        mime?.startsWith("image/") ||
                        url?.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i);

                      const fileExtension =
                        name?.split(".").pop()?.toUpperCase() ||
                        (isImage ? "IMAGE" : "FILE");

                      return (
                        <Box
                          key={index}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: `1px solid ${theme.palette.mode === "dark"
                                ? "#334155"
                                : "#e5e7eb"
                              }`,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#f9fafb",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            padding: "4px",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            },
                          }}
                          onClick={() => {
                            if (isImage) {
                              setSelectedAttachment({ url, name, mime });
                              setImagePreviewOpen(true);
                            } else {
                              window.open(url, "_blank");
                            }
                          }}
                        >
                          {isImage ? (
                            <img
                              src={url}
                              alt={name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                const parent = e.target.parentElement;
                                while (parent.firstChild) {
                                  parent.removeChild(parent.firstChild);
                                }
                                const fallbackDiv =
                                  document.createElement("div");
                                fallbackDiv.style.cssText = `
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                  `;
                                const iconSpan = document.createElement("span");
                                iconSpan.style.cssText = `
                    font-size: 32px;
                    color: ${theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280"
                                  };
                  `;
                                iconSpan.textContent = "📄";
                                fallbackDiv.appendChild(iconSpan);
                                const labelSpan =
                                  document.createElement("span");
                                labelSpan.style.cssText = `
                    font-size: 0.6rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    color: ${theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280"
                                  };
                    margin-top: 2px;
                  `;
                                labelSpan.textContent = fileExtension;
                                fallbackDiv.appendChild(labelSpan);
                                parent.appendChild(fallbackDiv);
                              }}
                            />
                          ) : (
                            <>
                              <DescriptionIcon
                                sx={{
                                  fontSize: 32,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#94a3b8"
                                      : "#6b7280",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#94a3b8"
                                      : "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                {fileExtension}
                              </Typography>
                            </>
                          )}
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              flexWrap="wrap"
              useFlexGap
            >
              {hasPermission("tasks.update") && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setSelectedTask(selectedTaskForDetails);
                    setSelectedTaskForDetails(null);
                    setShowEditModal(true);
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "#f59e0b",
                    color: "#d97706",
                    textTransform: "none",
                  }}
                >
                  Edit Task
                </Button>
              )}
              {hasPermission("tasks.delete") && (
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    handleDeleteClick(selectedTaskForDetails);
                    setSelectedTaskForDetails(null);
                  }}
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    textTransform: "none",
                  }}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Task
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "primary.main",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{taskToDelete?.title}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setSelectedAttachment(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#111827" : "#ffffff",
            maxWidth: "800px",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={() => {
              setImagePreviewOpen(false);
              setSelectedAttachment(null);
            }}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.8)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Stack spacing={2}>
            <Typography
              variant="caption"
              sx={{
                color: "#d97706",
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {selectedAttachment?.name || "Image"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                bgcolor: "background.default",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {selectedAttachment && (
                <img
                  src={selectedAttachment.url}
                  alt={selectedAttachment.name || "Image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    const parent = e.target.parentElement;
                    const errorDiv = document.createElement("div");
                    errorDiv.style.cssText = `
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 40px;
                gap: 16px;
                width: 100%;
              `;
                    errorDiv.innerHTML = `
                <span style="font-size: 48px;">🖼️</span>
                <p style="color: #6b7280; margin: 0;">Failed to load image</p>
                <button style="
                  padding: 8px 16px;
                  border: 1px solid #f59e0b;
                  border-radius: 8px;
                  background: transparent;
                  color: #f59e0b;
                  cursor: pointer;
                " onclick="window.open('${selectedAttachment.url}', '_blank')">
                  Open in New Tab
                </button>
              `;
                    parent.appendChild(errorDiv);
                  }}
                />
              )}
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(selectedAttachment?.url, "_blank")}
                sx={{
                  borderColor: "#f59e0b",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.1)",
                  },
                }}
              >
                Open in New Tab
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setImagePreviewOpen(false);
                  setSelectedAttachment(null);
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Task;
